import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { 
  DynamicForm, 
  Field, 
  FormValidationResult, 
  FormFieldConfig 
} from '../modules/configuration/form-builder/models/form-builder.model';

@Injectable({
  providedIn: 'root'
})
export class FormBuilderService {

  constructor(private http: HttpClient) {}


  getFieldsByModule(payload: any): Observable<any> {
    return this.http.post((environment as any).getLeadsFields,payload);
  }

  /**
   * Get all available modules
   */
  getModules(): Observable<any> {
    return this.http.get(`${(environment as any).apiUrl}/api/modules`);
  }

  // ============ Form Management APIs ============
  


  /**
   * Get form by ID
   */
  getFormById(formId: string): Observable<any> {
    return this.http.get(`${(environment as any).apiUrl}/api/forms/${formId}`);
  }

  /**
   * Create a new form
   */
  createForm(form: any): Observable<any> {
    form.form_field_config = form.fields; // Map fields to form_field_config for backend
    return this.http.post((environment as any).createDynamicForm, form);
  }

  /**
   * Update existing form
   */
  updateForm(form: any): Observable<any> {
    return this.http.post((environment as any).updateDynamicForm, form);
  }

  /**
   * Delete form
   */
  deleteForm(payload: any): Observable<any> {
    return this.http.post((environment as any).deleteDynamicForm, payload);
  }

  /**
   * Publish form (triggers backend validation)
   */
  publishForm(formId: string): Observable<any> {
    return this.http.post(`${(environment as any).apiUrl}/api/forms/${formId}/publish`, {});
  }

  /**
   * Unpublish form
   */
  unpublishForm(formId: string): Observable<any> {
    return this.http.post(`${(environment as any).apiUrl}/api/forms/${formId}/unpublish`, {});
  }

  /**
   * Validate form before publishing
   */
  validateForm(form: DynamicForm): Observable<any> {
    return this.http.post(`${(environment as any).apiUrl}/api/forms/validate`, form);
  }

  // ============ Public Form APIs ============
  
  /**
   * Get public form by token (for rendering)
   */
  getPublicForm(publicToken: string): Observable<any> {
    return this.http.post((environment as any).getDynamicFormByToken, { public_token: publicToken });
  }

  /**
   * Submit public form
   */
  submitPublicForm(payload: any): Observable<any> {
    return this.http.post((environment as any).submitPublicForm, payload);
  }

  // ============ Helper Methods ============
  
  /**
   * Get UI types compatible with a data type
   */
  getCompatibleUITypes(dataType: string): string[] {
    const mapping: { [key: string]: string[] } = {
      'string': ['text', 'textarea', 'email', 'tel', 'select'],
      'text': ['textarea'],
      'number': ['number'],
      'boolean': ['checkbox'],
      'date': ['date'],
      'enum': ['select', 'radio', 'multiselect']
    };
    return mapping[dataType] || ['text'];
  }

  /**
   * Client-side validation of form configuration
   */
  validateFormConfig(form: DynamicForm): FormValidationResult {

    console.log("Validating form config:", form);

    const errors: string[] = [];

    // Check form name
    if (!form.form_name || form.form_name.trim() === '') {
      errors.push('Form name is required');
    }

    // Check module selection
    if (!form.module_name && !form.module_id) {
      errors.push('Module selection is required');
    }

    // Check if form has fields
    if (!form.fields || form.fields.length === 0) {
      errors.push('Form must have at least one field');
    }

    // Validate each field
    if (form.fields) {
      const moduleIds = new Set<string>();
      
      form.fields.forEach((fieldConfig, index) => {
        // Check if field exists
        // if (!fieldConfig.field_id) {
        //   errors.push(`Field at position ${index + 1} is missing field_id`);
        // }

        // Check UI type
        if (!fieldConfig.ui_type) {
          errors.push(`Field "${fieldConfig.field?.label || 'Unknown'}" is missing UI type`);
        }

        // Check if all fields belong to same module
        if (fieldConfig.field && fieldConfig.field.module_id) {
          moduleIds.add(fieldConfig.field.module_id);
        }

        // UI type compatibility validation disabled - allow any combination
        // if (fieldConfig.field && fieldConfig.ui_type) {
        //   const compatibleTypes = this.getCompatibleUITypes(fieldConfig.field.data_type);
        //   if (!compatibleTypes.includes(fieldConfig.ui_type)) {
        //     errors.push(`Field "${fieldConfig.field.label}": UI type "${fieldConfig.ui_type}" is not compatible with data type "${fieldConfig.field.data_type}"`);
        //   }
        // }

        // Check options for select fields
        if (['select', 'radio', 'multiselect'].includes(fieldConfig.ui_type)) {
          if (fieldConfig.field?.data_type === 'enum' && !fieldConfig.field.options) {
            errors.push(`Field "${fieldConfig.field?.label || 'Unknown'}": Enum fields require options`);
          }
        }
      });

      // Ensure all fields belong to the same module
      if (moduleIds.size > 1) {
        errors.push('All fields must belong to the same module');
      }

      // Ensure fields belong to selected module
      if (form.module_name && moduleIds.size > 0 && !moduleIds.has(form.module_name)) {
        errors.push('Fields do not match the selected module');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Generate a shareable public URL for the form
   */
  getPublicFormUrl(publicToken: string): string {
    return `${window.location.origin}/#/p/${publicToken}`;
  }

  getDynamicFormList(organizationId: number, campusId: number): Observable<any> {
    const body = {
      organization_id: organizationId,
      campus_id: campusId
    };
    return this.http.post((environment as any).getDynamicFormList, body);
  }
}
